import { ReactElement, useState } from 'react';

import {
  ActionDialogButtons,
  ActionDialogContent,
  Button,
  Dialog,
  DialogContent,
  DialogTrigger,
  IconButton,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@opositandopn/ui';

import { BaseComponentProps } from '../../../../types/base-component.types';

import styles from './ConfirmStepButton.module.scss';

type ContainerProps = BaseComponentProps & {
  /** The icon the footer button carries. */
  icon: ReactElement;
  /** What the icon is painted in: the action's own colour, not the dialog's. */
  tone: 'danger' | 'secondary';
  /** Shown on hover. Left out where the icon speaks for itself, as the bin does. */
  tooltip?: string;
  title: string;
  subtitle?: string;
  /** What the confirming button reads — "Eliminar", "Reiniciar", … */
  confirmLabel: string;
  disabled?: boolean;
  /** The action is running: the dialog stays open and closes itself once it ends. */
  isPending: boolean;
  onConfirm: () => void;
};

/**
 * A step footer's destructive action: an icon that asks before it acts. The dialog
 * holds itself open for as long as the action runs and closes on its own when it
 * finishes, so the caller never has to drive `open`.
 */
function ConfirmStepButton({
  icon,
  tone,
  tooltip,
  title,
  subtitle,
  confirmLabel,
  disabled: disabledProp = false,
  isPending,
  onConfirm,
  testId = 'confirm-step-button',
}: ContainerProps) {
  const [open, setOpen] = useState(false);
  const [wasPending, setWasPending] = useState(false);
  const disabled = disabledProp || isPending;

  if (wasPending !== isPending) {
    setWasPending(isPending);

    if (!isPending) {
      setOpen(false);
    }
  }

  const trigger = (
    <DialogTrigger asChild={true}>
      <IconButton
        icon={icon}
        className={styles[tone]}
        disabled={disabled}
        testId={testId}
        onClick={() => setOpen(true)}
      />
    </DialogTrigger>
  );

  // A disabled button dispatches neither pointer nor focus events, so the tooltip
  // would stay shut on the very case it is there to explain — why the action is off.
  // The span takes the hover and the focus in the button's place.
  const anchor = disabled ? (
    <span className={styles['anchor']} tabIndex={0}>
      {trigger}
    </span>
  ) : (
    trigger
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {tooltip === undefined ? (
        trigger
      ) : (
        <Tooltip>
          <TooltipTrigger asChild={true}>{anchor}</TooltipTrigger>
          <TooltipContent className={styles['tooltip']}>
            {tooltip}
          </TooltipContent>
        </Tooltip>
      )}
      <DialogContent>
        <ActionDialogContent title={title} subtitle={subtitle}>
          <ActionDialogButtons>
            <Button
              variant="outline-primary"
              disabled={disabled}
              onClick={() => setOpen(false)}
            >
              Cancelar
            </Button>
            <Button
              variant="danger"
              disabled={disabled}
              loading={isPending}
              onClick={onConfirm}
            >
              {confirmLabel}
            </Button>
          </ActionDialogButtons>
        </ActionDialogContent>
      </DialogContent>
    </Dialog>
  );
}

export { ConfirmStepButton };
